
/*
 * Copyright (c) 2023. Data Smart Technologies CC
 * https://www.datasmart.co.za
 * View license agreement and terms of use at https://www.datasmart.co.za/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2023-06-29
 */

import React, { useEffect, useState } from "react";
import { Button, Card, Container, Modal } from "react-bootstrap";
import { useAuth } from "../../../../contexts/AuthContext";
import { ReactFlow, Background, Controls, Position, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver'
import { CastType, IQuery } from "../../../../component/solution/Interfaces";
import { formatDateFull } from "../../../../component/solution/utils/AppUtil";
import { useNavigate } from "react-router-dom";
import { FileCheck, Send, PlaneLanding, Pencil, RotateCcw, BookOpenText, Signature, FilePenLine } from "lucide-react";


const currentStep: object = { 
    width: 80, 
    height: 40, 
    border: '1px dashed red', // Add a bold red border
    backgroundColor: '#f0f0f0', // Optional: background color
};
const normalStep: object = { 
    width: 80, 
    height: 40, 
};

const preAuthStep: object = {
    width: 80, 
    height: 40,
    display: 'block',
}

const preAuthStepInit: object = { 
    width: 80, 
    height: 40, 
    border: '1px dashed green', // Add a bold green border
    backgroundColor: '#e0f8e0', // Optional: background color  #f0f0f0
};

// WorkFlow - START  
const initNodes = [
    {
        id: 'FETCHED', // required
        position: { x: 30, y: 80 }, // required
        data: { label: 'Fetch' }, // required
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'SEND', // required
        position: { x: 140, y: 80 }, // required
        data: { label: 'Send' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'SENT', // required
        position: { x: 250, y: 80 }, // required
        data: { label: 'Sent' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'SUCCESS', // required
        position: { x: 360, y: 80 }, // required
        data: { label: 'Success' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'ARRIVED', // required
        position: { x: 470, y: 80 }, // required
        data: { label: 'Arrived' }, // required
        targetPosition: Position.Left,
        sourcePosition: Position.Right,
        style: normalStep
    },
    {
        id: 'CLOSED', // required
        position: { x: 580, y: 80 }, // required
        data: { label: 'Closed' }, // required
        targetPosition: Position.Left,
        style: normalStep
    }, 
    {
        id: 'ERROR', // required
        position: { x: 30, y: 10 }, // required
        data: { label: 'Error' }, // required
        targetPosition: Position.Right,
        style: normalStep
    },  
    {
        id: 'PREAUTH', // required
        position: { x: 415, y: 10 }, // required
        data: { label: 'Pre-Auth' }, // required
        style: normalStep,
    }                                                     
  ];

  const stepEdges = [
    { id: 'FETCHED-SEND', source: 'FETCHED', target: 'SEND', label: ">", type: 'step' },
    { id: 'SEND-SENT', source: 'SEND', target: 'SENT', label: ">", type: 'step' },
    { id: 'SENT-ERROR', source: 'SENT', target: 'ERROR', label: "<", type: 'step' },
    { id: 'SENT-SUCCESS', source: 'SENT', target: 'SUCCESS', label: ">", type: 'step' },
    { id: 'SUCCESS-ARRIVED', source: 'SUCCESS', target: 'ARRIVED', label: ">", type: 'step' },
    { id: 'ARRIVED-CLOSED', source: 'ARRIVED', target: 'CLOSED', label: ">", type: 'step' },
  ];

enum ShipmentStatus {
    TO_FETCH = "TO_FETCH", 
    FETCHED = "FETCHED",
    TO_SEND = "TO_SEND",
    SENT = "SENT",
    SUCCESS = "SUCCESS", // M24L Validation Success
    ERROR = "ERROR", // M24L Validation Failed
    ARRIVED = "ARRIVED",
    CLOSED = "CLOSED",
    RECEIVED = "RECEIVED",
    CANCELLED = "CANCELLED", //new 
}

enum PreAuthStatus {
    DONE = "DONE",
    MANUAL = "MANUAL",
    PRE_AUTH = "PRE_AUTH", 
}

interface IShipmentSetStatus {
    shipment_num: string,
    status: ShipmentStatus
}



export const ShipmentsManage: React.FC = () => {

    const navigate = useNavigate(); // hook for navigation

    const [shipmentNo, setShipmentNo] = useState<string>("");
    const [shipmentStatusStep, setShipmentStatusStep] = useState<string>("");
    const [sendShipment, setSendShipment] = useState<boolean>(false);
    const {fetchData}  = useAuth();
    const [stepNodes, setStepNodes] = useState<any[]>(initNodes);
    const [selectedShipmentNo, setSelectedShipmentNo] =  useState<string>("");

    // Processed Shipments End up here.
    const [shipmentElements, setShipmentElements] = useState<React.ReactElement[]>([]);
    // Processed Shipment Summary Lines end up here.
    const [shipmentSummaryElements, setShipmentSummaryElements] = useState<React.ReactElement[]>([]);

    const [currency, setCurrency] = useState<string>("");
    const [exchangeRate, setExchangeRate] = useState<string>("");
    const [isCurrencyValid, setIsCurrencyValid] = useState<boolean>(false);  
    const [isExchangeRateValid, setIsExchangeRateValid] = useState<boolean>(false); 
    const shipmentCurrencyExchangeQueryUrl = `${process.env.REACT_APP_BASE_URL}/functions/SHIPMENT-SET-CURRENCY-AND-EXCHANGE/run2`;
    const shipmentProcessManualUrl = `${process.env.REACT_APP_BASE_URL}/functions/PROCESS-SHIPMENT-MANUAL/run2`;

    const [landedCost, setLandedCost] = useState<string>("");
    const landedCostQueryUrl = `${process.env.REACT_APP_BASE_URL}/functions/PROCESS-SHIPMENT-PREAUTH/run2`

    // Modal Show & Close
    const [showModal, setShowModal] = useState<boolean>(false);
    const [showManualModal, setShowManualModal] = useState<boolean>(false);
    const [showPreAuthModal, setShowPreAuthModal] = useState<boolean>(false);
    const [showLandedCostModal, setShowLandedCostModal] = useState<boolean>(false);
    const [showCancelShipmentModal, setShowCancelShipmentModal] = useState<boolean>(false);
    

    useEffect(() => {
       
        fetchShipments();
        const intervalId = setInterval(fetchShipments, 15000);

        // Cleanup function to stop the interval when component unmounts
        return () => clearInterval(intervalId);
    }, []);

    // Get the current Shipments
    const fetchShipments = async () => {

        const shipmentQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;

        const shipmentQuery: IQuery = {
            tableName: "sa_m24l_shipment",
            select: [
                {
                  fullColumnName: "carton_closed_count",
                  aggregate: null,
                },
                {
                    fullColumnName: "carton_count",
                    aggregate: null,
                },
                {
                    fullColumnName: "create_goods_receipt",
                    aggregate: null,
                },
                {
                    fullColumnName: "currency",
                    aggregate: null,
                },
                {
                    fullColumnName: "excess_items",
                    aggregate: null,
                },
                {
                    fullColumnName: "exchange_rate",
                    aggregate: null,
                },
                {
                    fullColumnName: "integration_status",
                    aggregate: null,
                },
                {
                    fullColumnName: "landed_cost_doc_num",
                    aggregate: null,
                },
                {
                    fullColumnName: "pre_auth_status",
                    aggregate: null,
                },
                {
                    fullColumnName: "shipment_num",
                    aggregate: null,
                },
                {
                    fullColumnName: "notifications.repeat_count",
                    aggregate: "sum",
                },
                {
                    fullColumnName: "notifications.reference",
                    aggregate: "min",
                },
                {
                    fullColumnName: "created",
                    aggregate: null,
                },
                {
                    fullColumnName: "updated",
                    aggregate: null,
                },
            ],
            groupBy: [
              "id"
            ],
            join: [{
                relationship: "notifications",
                joinType: "LEFT"
            }],
            orderBy: [
                {
                    fullColumnName: "updated",
                    direction: "DESC",
                    cast: CastType.STRING
                },
                {
                    fullColumnName: "created",
                    direction: "DESC",
                    cast: CastType.STRING
                }                                  
            ],
            where: [],
            mainTableOnly: false,
            includeExtraColumns: false,
            includeControlColumns: false,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            pageNum: 0,
            pageSize: 100,
            frontEndOnly: null
        }
   
        fetchData(shipmentQueryUrl, {method:"POST", body: JSON.stringify(shipmentQuery)}, processShipments);
    }

    //default span buttons design (disabled)
    const SpanReFetchBtn = () => {
        return (
        <span className="btn btn-light span-buttons-default" title="Re-Fetch">
            <RotateCcw className="icon-default" />
         </span> 
        ) 
    }

    const SpanSendShipmentBtn = () => {
        return (
        <span className="btn btn-light span-buttons-default" title="Send Shipment">
            <Send className="icon-default" />
         </span> 
        ) 
    }

    const SpanPreAuthBtn = () => {
        return (
        <span className="btn btn-light span-buttons-default" title="Pre-Auth">
            <FilePenLine className="icon-default" />
         </span> 
        ) 
    }

    const SpanManualBtn = () => {
        return (
        <span className="btn btn-light span-buttons-default" title="Manual">
            <Pencil className="icon-default" />
         </span> 
        ) 
    }

    const SpanLandedCostBtn = () => {
        return (
         <span className="btn btn-light span-buttons-default" title="Landed Cost">
            <PlaneLanding className="icon-default" />
         </span> 
        ) 
    }
 
    const SpanCancelShipmentBtn = () => {
        return (
         <span className="btn btn-danger span-buttons-default-error" title="Cancel Shipment">
            <span className="icon-default-error" style={{ fontSize: "15px" }}>✘</span>
         </span> 
        ) 
    }


    const SpanShipmentDetailedBtn = () => {
        return (
         <span className="btn btn-light span-buttons-default" title="Shipment Details">
            <BookOpenText className="icon-default" />
         </span> 
        ) 
    }

    const progressBarTDStyle = {
        minWidth: '150px', 
        maxWidth: '160px',
        padding: '4px 6px'
    }

    const tdSmallerStyle = {
        padding: '4px 6px'
    }

    // Process the Result into a table for display purposes.
    const processShipments = (result: any) => {

        console.log(`This is the data for processing shipments`, result);

        const shipments: any[] = result.data;

        // Build React elements and set state
        const shipmentElements: React.ReactElement[] = shipments.map((shipment, index) => (
            <tr key={index}>
                {shipment.data.integration_status === ShipmentStatus.TO_FETCH &&
                    <>                    
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td> 
                        <td style={tdSmallerStyle}>{shipment.data?.landed_cost_doc_num}</td>   
                      
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" style={{width: 'fit-content'}}>
                                <div className="progress-bar bg-success" role="progressbar" style={{width: "1%"}} aria-valuenow={5} aria-valuemin={0} aria-valuemax={100}>1%</div>
                            </div>                             
                        </td> 
                        <td style={tdSmallerStyle}>  
                           { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-warning text-white text-center" style={tdSmallerStyle}>
                            &nbsp;
                            <div className="spinner-border spinner-border-sm text-white" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <span>&nbsp;&nbsp;Fetching...</span>
                        </td> 
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td>
                        <td style={tdSmallerStyle}>
                            <div className="d-flex justify-content-center gap-1"> 
                                <SpanReFetchBtn/>   
                                <SpanSendShipmentBtn/>            
                                <SpanPreAuthBtn/>
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <SpanShipmentDetailedBtn/> 
                                <button
                                    className="btn btn-danger"
                                    style={{ margin: "1px" }}
                                    title="Cancel Shipment"
                                    onClick={() => handleCancelShimpmentModal(shipment.data?.shipment_num)}
                                 >
                                    <span style={{ fontSize: "15px" }}>✘</span>
                                </button>  
                            </div>
                        </td>                    
                    </>                               
                }
                {shipment.data.integration_status === ShipmentStatus.FETCHED &&
                    <>                  
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>   
                        <td style={tdSmallerStyle}>{shipment.data?.landed_cost_doc_num}</td>                     
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" style={{width: 'fit-content'}}>
                                <div className="progress-bar bg-success" role="progressbar" style={{width: "5%"}} aria-valuenow={5} aria-valuemin={0} aria-valuemax={100}>5%</div>
                            </div>                             
                        </td> 
                        <td style={tdSmallerStyle}>  
                           { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-warning text-white text-center" style={tdSmallerStyle}>
                            FETCHED
                        </td> 
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td>
                        <td style={tdSmallerStyle}>
                            <div className="d-flex justify-content-center gap-1"> 
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Re-Fetch"
                                    onClick={() => handleShipmentStatusChange(ShipmentStatus.TO_FETCH, shipment.data?.shipment_num)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                    <RotateCcw style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>  
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Send Shipment"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate, true)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                   <Send style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>           
                                <SpanPreAuthBtn/>
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                 <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <button
                                    className="btn btn-danger"
                                    style={{ margin: "1px" }}
                                    title="Cancel Shipment"
                                    onClick={() => handleCancelShimpmentModal(shipment.data?.shipment_num)}
                                 >
                                    <span style={{ fontSize: "15px" }}>✘</span>
                                </button>                           
                            </div>
                        </td>
                    </>
                }
                {shipment.data.integration_status === ShipmentStatus.TO_SEND &&
                    <>                 
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>         
                        <td style={tdSmallerStyle}>{shipment.data?.landed_cost_doc_num}</td>            
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" style={{width: 'fit-content'}}>
                                <div className="progress-bar bg-success" role="progressbar" style={{width: "10%"}} aria-valuenow={10} aria-valuemin={0} aria-valuemax={100}>10%</div>
                            </div>                        
                        </td>   
                        <td style={tdSmallerStyle}>  
                           { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-warning text-white text-center" style={tdSmallerStyle}>
                            Sending ...
                        </td> 
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td> 
                        <td style={tdSmallerStyle}>
                            <div className="d-flex justify-content-center gap-1"> 
                                <SpanReFetchBtn/>   
                                <SpanSendShipmentBtn/>               
                                <SpanPreAuthBtn/>
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                 <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <button
                                    className="btn btn-danger"
                                    style={{ margin: "1px" }}
                                    title="Cancel Shipment"
                                    onClick={() => handleCancelShimpmentModal(shipment.data?.shipment_num)}
                                 >
                                    <span style={{ fontSize: "15px" }}>✘</span>
                                </button>                   
                            </div>
                        </td>                  
                    </>
                }
                {shipment.data.integration_status === ShipmentStatus.SENT &&
                    <>                  
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>   
                        <td style={tdSmallerStyle}>{shipment.data?.landed_cost_doc_num}</td>                   
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" style={{width: 'fit-content'}}>
                                    <div className="progress-bar bg-success" role="progressbar" style={{width: "15%"}} aria-valuenow={15} aria-valuemin={0} aria-valuemax={100}>15%</div>
                            </div>                        
                        </td>   
                        <td style={tdSmallerStyle}>  
                            { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-warning text-white text-center" style={tdSmallerStyle}>
                            SENT
                        </td>  
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td> 
                        <td style={tdSmallerStyle}>
                            <div className="d-flex justify-content-center gap-1"> 
                                <SpanReFetchBtn/>   
                                <SpanSendShipmentBtn/>               
                                <SpanPreAuthBtn/>
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                 <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <SpanCancelShipmentBtn/>
                            </div> 
                        </td>                   
                    </>
                }                    
                {shipment.data.integration_status === ShipmentStatus.SUCCESS &&
                    <>                   
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>  
                        <td style={tdSmallerStyle} >{shipment.data?.landed_cost_doc_num}</td>                     
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" style={{width: 'fit-content'}}>
                                    <div className="progress-bar bg-success" role="progressbar" style={{width: "20%"}} aria-valuenow={20} aria-valuemin={0} aria-valuemax={100}>20%</div>
                            </div>                            
                        </td>  
                        <td style={tdSmallerStyle}>  
                           { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-warning text-white text-center"  style={tdSmallerStyle} >
                            SUCCESS - Validated
                        </td> 
                        <td style={tdSmallerStyle} > {formatDateFull(shipment.control?.updated)}</td>  
                        <td style={tdSmallerStyle} >
                            <div className="d-flex justify-content-center gap-1"> 
                                <SpanReFetchBtn/>   
                                <SpanSendShipmentBtn/>    
                                { 
                                  shipment.data?.pre_auth_status === null ? 
                                    <button type="button" className="btn btn-outline-light shipment-btn" title="Pre-Auth"
                                        onClick={() => handlePreAuthModal(shipment.data?.shipment_num)}
                                        style={{border:'1px solid #dbdad9'}}
                                    > 
                                        <FilePenLine  style={{ width: "20px", height: "20px", color:'#343a40' }} />
                                    </button>  
                                  : 
                                    <SpanPreAuthBtn/>
                                 }          
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                 <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <SpanCancelShipmentBtn/>                            
                            </div>
                        </td>                     
                    </>
                } 
                {shipment.data.integration_status === ShipmentStatus.ERROR &&
                    <>                  
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>      
                        <td style={tdSmallerStyle}>{shipment.data?.landed_cost_doc_num}</td>                  
                         
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" style={{width: 'fit-content'}}>
                                <div className="progress-bar bg-danger" role="progressbar" style={{width: "0%"}} aria-valuenow={0} aria-valuemin={0} aria-valuemax={100}>0%</div>
                            </div>                            
                        </td>  
                        <td style={tdSmallerStyle}>  
                           { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-danger text-white text-center" style={tdSmallerStyle}>
                            ERROR - Validation Error
                        </td>  
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td>  
                        <td>
                        <div className="d-flex justify-content-center gap-1"> 
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Re-Fetch"
                                    onClick={() => handleShipmentStatusChange(ShipmentStatus.TO_FETCH, shipment.data?.shipment_num)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                    <RotateCcw style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>  
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Re-Fetch"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate, true)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                   <Send style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>           
                                <SpanPreAuthBtn/>
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                 <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <button
                                    className="btn btn-danger"
                                    style={{ margin: "1px" }}
                                    title="Cancel Shipment"
                                    onClick={() => handleCancelShimpmentModal(shipment.data?.shipment_num)}
                                 >
                                    <span style={{ fontSize: "15px" }}>✘</span>
                                </button>   
                            </div>
                        </td>                 
                    </>
                } 
                {shipment.data.integration_status === ShipmentStatus.ARRIVED &&
                    <>                 
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>   
                        <td style={tdSmallerStyle}>{shipment.data?.landed_cost_doc_num}</td>                   
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" style={{width: 'fit-content'}}>
                                <div className="progress-bar bg-success" role="progressbar" style={{width: "20%"}} aria-valuenow={20} aria-valuemin={0} aria-valuemax={100}>25%</div>
                            </div>
                        </td>  
                        <td style={tdSmallerStyle}>  
                          { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-warning text-white text-center" style={tdSmallerStyle} >
                            ARRIVED
                        </td>         
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td>  
                        <td style={tdSmallerStyle} >
                            <div className="d-flex justify-content-center gap-1"> 
                                <SpanReFetchBtn/>   
                                <SpanSendShipmentBtn/>               
                                { shipment.data?.pre_auth_status === null ? 
                                    <button type="button" className="btn btn-outline-light shipment-btn" title="Pre-Auth"
                                        onClick={() => handlePreAuthModal(shipment.data?.shipment_num)}
                                        style={{border:'1px solid #dbdad9'}}
                                    > 
                                        <FilePenLine  style={{ width: "20px", height: "20px", color:'#343a40' }} />
                                    </button>  
                                  : 
                                    <SpanPreAuthBtn/>
                                 } 
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentSummary(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                 >
                                  <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <SpanCancelShipmentBtn/>
                            </div>
                        </td>           
                    </>
                } 
                {shipment.data.integration_status === ShipmentStatus.CLOSED &&
                    <>               
                       <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>       
                        <td style={tdSmallerStyle} >{shipment.data?.landed_cost_doc_num}</td>                  
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" >
                                <div className="progress-bar bg-success" role="progressbar" style={{width: "100%"}} aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>100%</div>
                            </div>                            
                        </td>   
                        <td style={tdSmallerStyle}>  
                           { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-success text-white text-center" style={tdSmallerStyle}>
                            CLOSED
                        </td>
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td>   
                        <td style={tdSmallerStyle}>
                            <div className="d-flex justify-content-center gap-1"> 
                                <SpanReFetchBtn/>   
                                <SpanSendShipmentBtn/>               
                                <SpanPreAuthBtn/>
                                <SpanManualBtn/>
                                <SpanLandedCostBtn/>
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentVariance(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                >
                                 <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <SpanCancelShipmentBtn/>
                            </div> 
                        </td>                    
                    </>
                } 
                {shipment.data.integration_status === ShipmentStatus.RECEIVED &&
                    <>                   
                        <td style={{ position: "relative", padding:'4px 6px' }}> 
                           { shipment.data.shipment_num === shipment.data?.["notifications.reference"] && 
                              <p className="text-center" style={{ position: "absolute",  top: "0", right: "0", marginRight: "2px", fontSize: "10px", font: "bold", padding: "2px 4px 0px 4px", background: "#d9534f", cursor: 'pointer'}}
                                 onClick={() => navigate('/m24l/notifications-new', { state: { shipment_num: shipment.data.shipment_num, category:''} })}
                                 title="This shipment has an error notification"
                               > 
                                <b style={{color: "white"}}>{shipment.data?.["notifications.repeat_count"]}</b>
                              </p> 
                            }
                            {shipment.data?.shipment_num}
                        </td>             
                        <td style={tdSmallerStyle}>{shipment.data?.landed_cost_doc_num}</td>           
                        <td style={progressBarTDStyle}>
                            <div className="progress w-100" >
                                <div className="progress-bar bg-success" role="progressbar" style={{width: "100%"}} aria-valuenow={100} aria-valuemin={0} aria-valuemax={100}>100%</div>
                            </div>                            
                        </td> 
                        <td style={tdSmallerStyle}>  
                           { shipment.data?.pre_auth_status !== null && 'Pre-Auth' }
                         </td> 
                        <td className="bg-success text-white text-center" style={tdSmallerStyle} >
                            RECEIVED
                        </td> 
                        <td style={tdSmallerStyle}>{formatDateFull(shipment.control?.updated)}</td>
                        <td style={tdSmallerStyle}>
                            <div className="d-flex justify-content-center gap-1"> 
                                <SpanReFetchBtn/>   
                                <SpanSendShipmentBtn/>               
                                <SpanPreAuthBtn/>
                                 {shipment.data?.pre_auth_status === null ? (
                                  <>
                                     <button type="button" className="btn btn-outline-light shipment-btn" title="Manual"
                                       onClick={() => handleManualModal(shipment.data?.shipment_num)}
                                       style={{border:'1px solid #dbdad9'}}
                                      >
                                     <Pencil style={{ width: "20px", height: "20px", color:'#343a40' }} />
                                    </button>   
                                    <SpanLandedCostBtn/>
                                  </>
                                ) : (
                                    <>
                                      <SpanManualBtn/>
                                      { shipment.data?.landed_cost_doc_num ? 
                                        (
                                          <SpanLandedCostBtn />
                                        )
                                        : 
                                        (
                                          <button type="button" className="btn btn-outline-light shipment-btn" title="Landed Cost"
                                            onClick={() => handleLandedCostModal(shipment.data?.shipment_num)}
                                            style={{ border: '1px solid #dbdad9' }}
                                          >
                                            <PlaneLanding style={{ width: "20px", height: "20px", color: '#343a40' }} />
                                          </button>
                                         )
                                        }
                                    </>
                                )}   
                                <button type="button" className="btn btn-outline-light shipment-btn" title="Shipment Details"
                                    onClick={() => fetchShipmentVariance(shipment.data?.shipment_num, shipment.data?.integration_status, shipment.data?.pre_auth_status, shipment.data?.currency, shipment.data?.exchange_rate)}
                                    style={{border:'1px solid #dbdad9'}}
                                 >
                                 <BookOpenText style={{ width: "20px", height: "20px", color:'#343a40' }}/>
                                </button>   
                                <SpanCancelShipmentBtn/>
                            </div> 
                        </td>    
                        {/* <td>{formatDateFull(shipment.control?.updated)}</td> */}
                    </>
                }                                              
            </tr>
        ));
    
        setShipmentElements(shipmentElements);
    }

    const handleShipmentStatusChange = async (status: ShipmentStatus, reFetchShipmentNo?: string) => {

        const fnShipmentSetStatusUrl: string = `${process.env.REACT_APP_BASE_URL}/${"functions/SHIPMENT-SET-STATUS/run"}`;

        const shipmentSetStatus: IShipmentSetStatus = {
            shipment_num: shipmentNo.trim(),
            status: status
        } 

        if(reFetchShipmentNo) {
            shipmentSetStatus.shipment_num = reFetchShipmentNo.trim();
        }

        // console.log(`ShipmentNo: ${shipmentSetStatus.shipment_num}`);
      
        if(shipmentSetStatus.shipment_num.length === 0) {
            return;
        }
        
        setShipmentNo("");

        fetchData(fnShipmentSetStatusUrl, {method:"POST", body: JSON.stringify(shipmentSetStatus)}, fetchShipments);
    };

    // Shipment Summary -START
    const fetchShipmentSummary = async (shipmentNo: string, shipmentStatus: ShipmentStatus, preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null, sendShipment:boolean = false) => {
        
        setShipmentNo(shipmentNo);
        setShipmentStatusStep(shipmentStatus);
        setSendShipment(sendShipment);

        setStepNodes((prevNodes) =>
            prevNodes.map((node) => {
                // Apply normal or currentStep styles to other nodes
                if (node.id === 'PREAUTH' && preAuthStatus !== null) {
                    return { ...node, style: preAuthStepInit }; 
                }
    
                return node.id === shipmentStatus
                    ? { ...node, style: currentStep }
                    : { ...node, style: normalStep };
            })
        );

        const shipmentLinesQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;

        const shipmentLinesQuery: IQuery = {
            tableName: "sa_m24l_shipment_line",
            select: [
            ],
            groupBy: [],
            join: [],
            orderBy: [
                {
                    fullColumnName: "item_code",
                    direction: "ASC",
                    cast: CastType.STRING
                }
            ],
            where: [
                {
                    fullColumnName: "shipment_num",
                    value: shipmentNo,
                    operator: "EQ",
                    cast: CastType.STRING
                }
            ],
            mainTableOnly: false,
            includeExtraColumns: false,
            includeControlColumns: true,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            // pageNum: null,
            // pageSize: null,
            frontEndOnly: null
        }

        fetchData(shipmentLinesQueryUrl, {method:"POST", body: JSON.stringify(shipmentLinesQuery)}, (result: any) =>  processShipmentSummary(result, preAuthStatus, shipmentCurrency, shipmentExchangeRate));
        setShowModal(true);
    }

    // Process the Result into a table for display purposes.
    const processShipmentSummary = (result: any,  preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null) => {


        const shipmentSummary: any[] = result.data;
        const shipmentTotalElements: React.ReactElement[] = [];
        const shipmentSummaryElements: React.ReactElement[] = [];
        const shipmentPreAuthorisedElements: React.ReactElement[] = [];

        if(preAuthStatus !== null){
           shipmentPreAuthorisedElements.push(
            <>
                <tr key='pre-authorised-header-sub'>
                    <td><b>Currency:</b></td>
                    <td><b>Exchange Rate:</b></td>
                </tr>
                <tr key='pre-authorised-body'>
                    <td className="fw-bold text-center">{shipmentCurrency}</td>
                    <td className="fw-bold text-center">{shipmentExchangeRate}</td>
                </tr> 
            </>)
        }
        
        let totalQuantity: number = 0;
        let totalReceived: number = 0;
        for(const key in shipmentSummary){
            totalQuantity += shipmentSummary[key].data?.qty_ordered;
            totalReceived = 0;
            
            const code = shipmentSummary[key].data?.item_code;
            const ordered = shipmentSummary[key].data?.qty_ordered;         

            shipmentSummaryElements.push(
                <tr key={key}>
                    <td className="text-center">{shipmentSummary[key].data?.item_code}</td>
                    <td className="text-center">{shipmentSummary[key].data?.qty_ordered}</td>
                </tr>
            );
        }

        shipmentTotalElements.push(
            <>
                <tr key={shipmentSummary.length}>
                    <td><b>Total Unique Items:</b></td>
                    <td><b>Total Units Ordered:</b></td>
                </tr>            
                <tr key={shipmentSummary.length + 1} >
                    <td className="fw-bold text-center">{shipmentSummary.length}</td>
                    <td className="fw-bold text-center">{totalQuantity}</td>
                </tr>
                <tr>
                    <td><b>Item Code</b></td>
                    <td><b>Quantity</b></td>
                </tr>                
            </>
        );
    
        setShipmentSummaryElements([...shipmentPreAuthorisedElements, ...shipmentTotalElements, ...shipmentSummaryElements]);
    }

    const confirmShipmentSummary = async (confirmShipmentNo: string) => {
        setShowModal(false);
        handleShipmentStatusChange(ShipmentStatus.TO_SEND, confirmShipmentNo);
    }
    // Shipment Summary -END

    // Shipment Variance - START
    const fetchShipmentVariance = async (shipmentNo: string, shipmentStatus: ShipmentStatus, preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null) => {
        
        //setConfirmShipmentNo(confirmShipmentNo);
        setShipmentNo(shipmentNo);
        setShipmentStatusStep(shipmentStatus);
        setSendShipment(false);

       /* setStepNodes((prevNodes) =>
            prevNodes.map((node) =>
              node.id === shipmentStatus
                ? { ...node, style:  currentStep}
                : { ...node, style:  normalStep}
            )
        ); */

        setStepNodes((prevNodes) =>
            prevNodes.map((node) => {
                // Apply normal or currentStep styles to other nodes
                if (node.id === 'PREAUTH' && preAuthStatus !== null) {
                    return { ...node, style: preAuthStepInit }; 
                }
    
                return node.id === shipmentStatus
                    ? { ...node, style: currentStep }
                    : { ...node, style: normalStep };
            })
        );

        const shipmentLinesQueryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;

        const shipmentLinesQuery: IQuery = {
            tableName: "sa_m24l_shipment_line",
            select: [
            ],
            groupBy: [],
            join: [],
            orderBy: [
                {
                    fullColumnName: "item_code",
                    direction: "ASC",
                    cast: CastType.STRING
                }
            ],
            where: [
                {
                    fullColumnName: "shipment_num",
                    value: shipmentNo,
                    operator: "EQ",
                    cast: CastType.STRING
                }
            ],
            mainTableOnly: false,
            includeExtraColumns: false,
            includeControlColumns: true,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            // pageNum: null,
            // pageSize: null,
            frontEndOnly: null
        }

        fetchData(shipmentLinesQueryUrl, {method:"POST", body: JSON.stringify(shipmentLinesQuery)}, (result: any) =>  processShipmentVariance(result, preAuthStatus, shipmentCurrency, shipmentExchangeRate));
        setShowModal(true);
    }

    const processShipmentVariance = (result: any, preAuthStatus: string | null, shipmentCurrency: string | null, shipmentExchangeRate: string | null) => {

        const shipmentSummary: any[] = result.data;
        const shipmentTotalElements: React.ReactElement[] = [];
        const shipmentVairanceElements: React.ReactElement[] = [];
        const shipmentSummaryElements: React.ReactElement[] = [];
        const shipmentPreAuthorisedElements: React.ReactElement[] = [];

        if(preAuthStatus !== null){
            shipmentPreAuthorisedElements.push(
             <>
                 <tr key='pre-authorised-header-sub' style={{ width: "100%"}}>
                     <td><b>Pre-Auth Status:</b></td>
                     <td><b>Currency:</b></td>
                     <td><b>Exchange Rate:</b></td>
                 </tr>
                 <tr key='pre-authorised-body' style={{ width: "100%"}}>
                     <td className="fw-bold text-center">{preAuthStatus}</td>
                     <td className="fw-bold text-center">{shipmentCurrency}</td>
                     <td className="fw-bold text-center">{shipmentExchangeRate}</td>
                 </tr> 
             </>)
         }
        
    
        let totalQuantity: number = 0;
        let totalReceived: number = 0;
        for(const key in shipmentSummary){
            totalQuantity += shipmentSummary[key].data?.qty_ordered;
            totalReceived += shipmentSummary[key].data?.qty_received;
            
            const code = shipmentSummary[key].data?.item_code;
            const ordered = shipmentSummary[key].data?.qty_ordered;
            const received = shipmentSummary[key].data?.qty_received;
            
            if (ordered === received){ 
                shipmentSummaryElements.push(
                    <tr key={key}>
                        <td className="text-center">{shipmentSummary[key].data?.item_code}</td>
                        <td className="text-center">{shipmentSummary[key].data?.qty_ordered}</td>
                        <td className="text-center">{shipmentSummary[key].data?.qty_received}</td>
                    </tr>
                );
            } else {
                shipmentVairanceElements.push(
                    <tr key={key}>
                        <td className="bg-danger text-white fw-bold text-center">{shipmentSummary[key].data?.item_code}</td>
                        <td className="bg-danger text-white fw-bold text-center">{shipmentSummary[key].data?.qty_ordered}</td>
                        <td className="bg-danger text-white fw-bold text-center">{shipmentSummary[key].data?.qty_received}</td>
                    </tr>
                );               
            }
        }

        shipmentTotalElements.push(
            <>
                <tr key={shipmentSummary.length}>
                    <td><b>Total Unique Items:</b></td>
                    <td><b>Total Units Ordered:</b></td>
                    <td><b>Total Units Received:</b></td>
                </tr>            
                <tr key={shipmentSummary.length + 1} >
                    <td className="fw-bold text-center">{shipmentSummary.length}</td>
                    <td className="fw-bold text-center">{totalQuantity}</td>
                    {totalQuantity !== totalReceived ? (
                            <td className="bg-danger text-white fw-bold text-center">{totalReceived}</td>
                        ) : (
                            <td className="bg-success text-dark fw-bold text-center">{totalReceived}</td>
                        )
                    }
                    
                </tr>
                <tr>
                    <td><b>Item Code</b></td>
                    <td><b>Quantity</b></td>
                    <td><b>Received</b></td>
                </tr>                
            </>
        );
    
        setShipmentSummaryElements([...shipmentPreAuthorisedElements, ...shipmentTotalElements, ...shipmentVairanceElements, ...shipmentSummaryElements]);
    }
    // Shipment Variance - END

    // Down load CSV - START
    // npm install xlsx-style file-saver
    // npm install @types/xlsx @types/file-saver --save-dev
    //
    const fetchShipmentVarianceExcel = async (confirmShipmentNo: string) => {
        
        //setConfirmShipmentNo(confirmShipmentNo);
        setShipmentNo(confirmShipmentNo);
        setSendShipment(false);

        const shipmentLinesQueryReportUrl: string = `${process.env.REACT_APP_REPORTS_URL}/shipment_report.xlsx&reportFilename=${confirmShipmentNo}.xlsx`;

        const shipmentLinesQuery: IQuery = {
            tableName: "sa_m24l_shipment_line",
            select: [
            ],
            groupBy: [],
            join: [],
            orderBy: [
                {
                    fullColumnName: "is_short",
                    direction: "DESC",
                    cast: CastType.BOOLEAN
                },
                {
                    fullColumnName: "item_code",
                    direction: "ASC",
                    cast: CastType.STRING
                } 
            ],
            where: [
                {
                    fullColumnName: "shipment_num",
                    value: confirmShipmentNo,
                    operator: "EQ",
                    cast: CastType.STRING
                }
            ],
            mainTableOnly: false,
            includeExtraColumns: false,
            includeControlColumns: true,
            modifiedAfter: null,
            touchedBefore: null,
            isDirty: null,
            // pageNum: null,
            // pageSize: null,
            frontEndOnly: null
        }

        fetchData(shipmentLinesQueryReportUrl, {method:"POST", body: JSON.stringify(shipmentLinesQuery)}, processShipmentVarianceExcel);
        setShowModal(true);
    }    

    const processShipmentVarianceExcel = async (result: any) => {


        // Create a download link for the Blob
        const url = URL.createObjectURL(result);

        // Create a temporary <a> element to trigger the download
        const a = document.createElement('a');
        a.href = url;
        //a.download = `${confirmShipmentNo}.xlsx`; // Set the default filename here
        a.download = `${shipmentNo}.xlsx`; // Set the default filename here
        document.body.appendChild(a);
        a.click();

    }    
    // Down load CSV - END    

     //hanlding pmanual authorization modal
    const handleManualModal = (shipmentNo: string) => {
        setShipmentNo(shipmentNo);
        setShowManualModal(true);
    }

    //hanlding pre-authorization modal
    const handlePreAuthModal = (shipmentNo: string) => {
        setShipmentNo(shipmentNo);
        setShowPreAuthModal(true);
    }

    //handling cost landed modal 
    const handleLandedCostModal = (shipmentNo: string) => {
        setShipmentNo(shipmentNo);
        setShowLandedCostModal(true);
    }

    const validateExchangeRate = (value: string) => {
        //[0, 00, 000, 0.0, 0.00, 0.000, 00.0, 00.00, 00.000, 000.0, 000.00, 000.000, abc]	-> invalid
        const regex = /^(?!0+(?:\.0+)?$)\d+(?:\.\d{1,4})?$/;
        return regex.test(value);
    };

    const handleOnChange = (e: any) => {
        const { value, name } = e.target;
    
        if (name === "currency") {
            setCurrency(value); 
            setIsCurrencyValid(value !== "");
        }
    
        if (name === "exchange_rate") {
            setExchangeRate(value); 
            const isValidExchangeRate = validateExchangeRate(value);
            setIsExchangeRateValid(isValidExchangeRate); 
        }

        if(name === "landed_cost"){
           setLandedCost(value);
        }
        
      };


    const handlePreAuthSubmit = async () => { 
        if(currency !== "" && exchangeRate !== ""){
            fetchData(shipmentCurrencyExchangeQueryUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": shipmentNo, "currency": currency, "exchange_rate": exchangeRate })}
                ,(result: any) => { console.log(result) });  
        }else{
            return;
        }

        //ressetting state and closing modal
        resetPreAuthStates();
    }

    const handleManualSubmit = async () => { 
        if(currency !== "" && exchangeRate !== ""){
            fetchData(shipmentProcessManualUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": shipmentNo, "currency": currency, "exchange_rate": exchangeRate })}
                ,(result: any) => { console.log(result) });  
        }else{
            return;
        }

        //ressetting state and closing modal
        resetPreAuthStates();
    }

    const handleLandedCostSubmit = async () => {
        if(landedCost !== ""){
            fetchData(landedCostQueryUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": shipmentNo, landed_cost_doc_num: landedCost })}
                ,(result: any) => { console.log(result) });  
        }else{
            return;
        }
        //ressetting state and closing modal
        resetLandedCostsState();
    }

    // handle cancel of shipment 
    const handleCancelShimpmentModal = (shipmentNo: string) => {
        setShowCancelShipmentModal(true);
        setSelectedShipmentNo(shipmentNo);
    }


    // This function re-fetches shipment data for display. 
    const refreshTable = () => {
        fetchShipments();
    }

    const handleCancelShipmentSubmit = () => {
        if(selectedShipmentNo !== ""){
            const fnShipmentSetStatusUrl: string = `${process.env.REACT_APP_BASE_URL}/${"functions/SHIPMENT-SET-STATUS/run"}`;
            fetchData(fnShipmentSetStatusUrl, 
                {method:"POST", body: JSON.stringify({ "shipment_num": selectedShipmentNo.trim() , "status": ShipmentStatus.CANCELLED })},
                (result: any) => {}, refreshTable);  
        }else{
            return;
        }
        //reset fields
        setSelectedShipmentNo(""); 
        setShowCancelShipmentModal(false);
    }


    const resetPreAuthStates = () => {
        setCurrency("");
        setExchangeRate("");
        setShowPreAuthModal(false);  
        setShowManualModal(false);  
        setShipmentNo("");   
        setIsCurrencyValid(false);
        setIsExchangeRateValid(false);
    }

    //resetting landed costs states
    const resetLandedCostsState = () => {
        setShipmentNo("");  
        setLandedCost("");
        setShowLandedCostModal(false);
    }
    
    const theadTrDarkTheme = {
        backgroundColor: '#3e444a'
    }

    const theadTdDarkTheme = {
        color: 'white', 
        fontWeight: '600'
    }
  
    return(
        <React.Fragment>
            <Container fluid className="p-0">

                <Card>
                    <Card.Header>
                        <Card.Title>{"Fetch, Confirm, Send & Receive Shipments"}</Card.Title>
                    </Card.Header>

                    <div className="ms-4 me-4" style={{}}>
                        <label className="form-check-label mt-0 mb-1" htmlFor="shipment"><b>Shipment No:</b></label>
                        <input 
                            id="shipment"
                            type="text"
                            value={shipmentNo}
                            onChange={(e) => setShipmentNo(e.target.value)}
                            placeholder="Shipment No."
                            className="form-control form-control-md mb-3 w-25"
                            autoComplete="off"
                        />
                        <button className="form-control form-control-md btn btn-secondary mb-3 w-25" onClick={() => handleShipmentStatusChange(ShipmentStatus.TO_FETCH)} data-bs-toggle="button" aria-pressed="false">Fetch Shipment</button>
  
                        {/* <p><b>Shipment Status:</b></p>  */}
                        <table className="table table-bordered w-100" >
                            <thead>
                                <tr style={theadTrDarkTheme} >
                                    <td><b style={theadTdDarkTheme}>Shipment</b></td>
                                    <td><b style={theadTdDarkTheme}>Landed Cost Doc Num</b></td>
                                    <td><b style={theadTdDarkTheme}>Progress</b></td>
                                    <td><b style={theadTdDarkTheme}>Pre-Auth Status</b></td>
                                    <td><b style={theadTdDarkTheme}>Status</b></td>
                                    <td><b style={theadTdDarkTheme}>Updated</b></td>
                                    <td><b style={theadTdDarkTheme}>Actions</b></td>
                                </tr>
                            </thead>
                            <tbody>
                                {shipmentElements}
                            </tbody>
                        </table>
                    </div>

                    <>
                        {/* Modal structure */}
                        <Modal show={showModal} onHide={() => { 
                            setShowModal(false);
                            setShipmentNo("");
                            } }>
                            <Modal.Header closeButton>
                            <Modal.Title>
                                {shipmentNo}
                            </Modal.Title>
                            <div>
                                &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                                    <Button variant="secondary" onClick={()=>fetchShipmentVarianceExcel(shipmentNo)}>
                                        Download
                                    </Button>
                                </div>                            
                            </Modal.Header>
                            <Modal.Body>
                                <p><b>Shipment Process:</b></p>
                                <div className="react-flow-wrapper" style={{ height: '120px', width: '700px' }}>
                                    
                                    <ReactFlow 
                                        nodes={stepNodes}
                                        edges={stepEdges}
                                        zoomOnScroll={false}  // Disable zooming with mouse scroll
                                        zoomOnPinch={false}   // Disable zooming with pinch gestures
                                        zoomOnDoubleClick={false}  // Disable zooming with double click
                                        panOnDrag={false}  // Disable panning by dragging the canvas
                                        minZoom={1}        // Lock the zoom to a fixed level
                                        maxZoom={1}        // Prevent zooming in or out                                                                       
                                    >
                                        <Controls
                                            showInteractive={false}
                                            showFitView={false}
                                            showZoom={false}
                                            orientation="horizontal"
                                        />
                                       
                                        
                                    </ReactFlow>
                                </div>                                          
                                <table className="table table-bordered">
                                    <thead>

                                    </thead>
                                    <tbody>
                                        {shipmentSummaryElements}
                                    </tbody>
                                </table>
                            </Modal.Body>
                            <Modal.Footer>
                            <Button variant="secondary" onClick={() => { 
                                setShowModal(false);
                                setShipmentNo("");
                             }}>
                                Close
                            </Button>
                            {sendShipment &&
                                <Button variant="danger" onClick={()=>confirmShipmentSummary(shipmentNo)}>
                                    Confirm & Send
                                </Button>
                            }
                            </Modal.Footer>
                        </Modal>


                        { /* Pre-Authorization Modal */ }
                        <Modal show={showPreAuthModal} onHide={() =>  {
                                resetPreAuthStates();
                             }}>
                            <Modal.Header closeButton>
                            <Modal.Title>
                                {shipmentNo}
                            </Modal.Title>                         
                            </Modal.Header>
                            <Modal.Body>
                                <p><b>Pre-Auth Process:</b></p>
                                                                
                                <table className="table table-bordered">
                                    <tbody style={{ display: 'flex', gap: '10px' }}>
                                        <select className="form-control form-control-md" 
                                            id="currency" 
                                            name="currency" 
                                            value={currency} 
                                            onChange={handleOnChange} 
                                            style={{ width: '220px', borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da'}}>
                                            <option value="">--Select Currency Option--</option>
                                            <option value="AUD">AUD</option>
                                            <option value="BWP">BWP</option>
                                            <option value="ETB">ETB</option>
                                            <option value="EUR">EUR</option>
                                            <option value="GBP">GBP</option>
                                            <option value="GHS">GHS</option>
                                            <option value="JPY">JPY</option>
                                            <option value="KES">KES</option>
                                            <option value="MUR">MUR</option>
                                            <option value="MWK">MWK</option>
                                            <option value="MZN">MZN</option>
                                            <option value="NGN">NGN</option>
                                            <option value="NZD">NZD</option>
                                            <option value="R">R</option>
                                            <option value="RWF">RWF</option>
                                            <option value="SGD">SGD</option>
                                            <option value="TZS">TZS</option>
                                            <option value="USD">USD</option>
                                            <option value="ZAR">ZAR</option>
                                            <option value="ZMW">ZMW</option>
                                        </select>
                                      <input 
                                        id="exchange_rate"
                                        value={exchangeRate}
                                        name="exchange_rate"
                                        onChange={handleOnChange}
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Enter Exchange Rate... Format - eg: 00.0000`} 
                                        className="form-control form-control-md"
                                        style={{borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da' }}>
                                       </input>
                                    </tbody>
                                </table>
                            </Modal.Body>
                            <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Button 
                                variant="primary" 
                                disabled={!isCurrencyValid || !isExchangeRateValid}
                                onClick={() => handlePreAuthSubmit()}>
                                Confirm
                            </Button>
                            <Button variant="secondary" onClick={() => {
                                resetPreAuthStates();
                             }}>
                                Close
                            </Button>
                            </Modal.Footer>
                        </Modal>

                        { /* Manual Modal */ }
                        <Modal show={showManualModal} onHide={() =>  {
                                resetPreAuthStates();
                             }}>
                            <Modal.Header closeButton>
                            <Modal.Title>
                                {shipmentNo}
                            </Modal.Title>                         
                            </Modal.Header>
                            <Modal.Body>
                                <p><b>Manual Process:</b></p>
                                                                
                                <table className="table table-bordered">
                                    <tbody style={{ display: 'flex', gap: '10px' }}>
                                        <select className="form-control form-control-md" 
                                            id="currency" 
                                            name="currency" 
                                            value={currency} 
                                            onChange={handleOnChange} 
                                            style={{ width: '220px', borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da'}}>
                                            <option value="">--Select Currency Option--</option>
                                            <option value="AUD">AUD</option>
                                            <option value="BWP">BWP</option>
                                            <option value="ETB">ETB</option>
                                            <option value="EUR">EUR</option>
                                            <option value="GBP">GBP</option>
                                            <option value="GHS">GHS</option>
                                            <option value="JPY">JPY</option>
                                            <option value="KES">KES</option>
                                            <option value="MUR">MUR</option>
                                            <option value="MWK">MWK</option>
                                            <option value="MZN">MZN</option>
                                            <option value="NGN">NGN</option>
                                            <option value="NZD">NZD</option>
                                            <option value="R">R</option>
                                            <option value="RWF">RWF</option>
                                            <option value="SGD">SGD</option>
                                            <option value="TZS">TZS</option>
                                            <option value="USD">USD</option>
                                            <option value="ZAR">ZAR</option>
                                            <option value="ZMW">ZMW</option>
                                        </select>
                                      <input 
                                        id="exchange_rate"
                                        value={exchangeRate}
                                        name="exchange_rate"
                                        onChange={handleOnChange}
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Enter Exchange Rate... Format - eg: 00.0000`} 
                                        className="form-control form-control-md"
                                        style={{borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da' }}>
                                       </input>
                                    </tbody>
                                </table>
                            </Modal.Body>
                            <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Button 
                                variant="primary" 
                                disabled={!isCurrencyValid || !isExchangeRateValid}
                                onClick={() => handleManualSubmit()}>
                                Confirm
                            </Button>
                            <Button variant="secondary" onClick={() => {
                                resetPreAuthStates();
                             }}>
                                Close
                            </Button>
                            </Modal.Footer>
                        </Modal>

                        { /* Landed Cost Modal */ }
                        <Modal show={showLandedCostModal} onHide={() =>  {
                                resetLandedCostsState();
                             }}>
                            <Modal.Header closeButton>
                            <Modal.Title>
                                {shipmentNo}
                            </Modal.Title>                         
                            </Modal.Header>
                            <Modal.Body>
                                <p><b>Landed Cost Process:</b></p>
                                                                
                                <table className="table table-bordered">
                                    <tbody style={{ display: 'flex', gap: '10px' }}>
                                       
                                      <input 
                                        id="landed_cost"
                                        value={landedCost}
                                        name="landed_cost"
                                        onChange={handleOnChange}
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Enter Landed Cost Doc Num.....`} 
                                        className="form-control form-control-md"
                                        style={{borderLeft: '1px solid #ced4da', borderRight: '1px solid #ced4da' }}>
                                       </input>
                                    </tbody>
                                </table>
                            </Modal.Body>
                            <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Button 
                                variant="primary" 
                                disabled={landedCost === "" ? true : false}
                                onClick={() => handleLandedCostSubmit()}>
                                Confirm
                            </Button>
                            <Button variant="secondary" onClick={() => {
                                resetLandedCostsState();
                             }}>
                                Close
                            </Button>
                            </Modal.Footer>
                        </Modal>




                        { /* Cancel Shipment Modal */ }
                        <Modal show={showCancelShipmentModal} onHide={() =>  {
                                setShowCancelShipmentModal(false);
                                setSelectedShipmentNo(""); 
                             }}>
                            <Modal.Header closeButton>
                            <Modal.Title>
                                {selectedShipmentNo}
                            </Modal.Title>                         
                            </Modal.Header>
                            <Modal.Body>
                                <p><b>Are you sure you want to cancel this shipment? This action cannot be undone.</b></p>
                            </Modal.Body>
                            <Modal.Footer style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                            <Button 
                                variant="primary" 
                                onClick={() => handleCancelShipmentSubmit()}
                                >
                                Confirm
                            </Button>
                            <Button variant="secondary" onClick={() => {
                                setShowCancelShipmentModal(false);
                                setSelectedShipmentNo(""); 
                            }} >
                                Close
                            </Button>
                            </Modal.Footer>
                        </Modal>

                    </>

                </Card>
            </Container>
        </React.Fragment>
    );
}

